/**
 * Per-page metadata for every public route.
 *
 * Every route previously served the single <title> and description baked into
 * public/index.html, so search engines saw one page. This is the single source
 * of truth for titles, descriptions, canonicals, and indexability.
 *
 * Kept as plain data (no React) so it stays unit-testable. Guards in
 * pageMeta.test.js enforce unique titles, length limits, and canonical form.
 *
 * NOTE: this is a client-rendered SPA, so metadata is applied at runtime.
 * It is correct for crawlers that execute JavaScript and becomes fully
 * effective if public routes are ever prerendered.
 */

import { CASE_TYPE_PAGES } from './caseTypePages';

export const SITE_URL = 'https://mycaseloan.com';

// Search engines truncate beyond roughly these lengths.
export const TITLE_MAX = 60;
export const DESCRIPTION_MIN = 50;
export const DESCRIPTION_MAX = 160;

const STATIC_PAGE_META = {
  home: {
    path: '/',
    title: 'MyCaseLoan — Pre-Settlement Legal Funding Marketplace',
    description:
      'Compare pre-settlement funding offers from multiple funders in one place. Attorney-verified applications and non-recourse advances — repay only if you win.',
  },
  apply: {
    path: '/apply',
    title: 'Apply for Pre-Settlement Funding | MyCaseLoan',
    description:
      'Apply for a pre-settlement advance in a few minutes. No credit check, nothing to pay upfront, and you repay only if your case succeeds.',
  },
  faq: {
    path: '/faq',
    title: 'Frequently Asked Questions | MyCaseLoan',
    description:
      'Answers about pre-settlement funding: what an advance costs, what happens if you lose your case, whether your credit is checked, and what your attorney does.',
  },
  contact: {
    path: '/contact',
    title: 'Contact Us | MyCaseLoan',
    description:
      'Questions about an application, the platform, or working with us? Send the MyCaseLoan team a message and we will reply by email.',
  },
  forLawFirms: {
    path: '/for-law-firms',
    title: 'Pre-Settlement Funding for Law Firms | MyCaseLoan',
    description:
      'Manage client funding requests in one place. Verify cases, share documents securely, and see every offer your clients receive — at no cost to your firm.',
    // The shared card speaks to someone with a case; this page is shared
    // between attorneys, so it carries its own.
    ogImage: '/og-law-firms.png',
  },
  forFunders: {
    path: '/for-funders',
    title: 'Case Flow for Legal Funders | MyCaseLoan',
    description:
      'Access pre-screened, attorney-verified funding applications with structured case data, a built-in offer builder, and a portfolio dashboard.',
  },
  terms: {
    path: '/terms',
    title: 'Terms of Service | MyCaseLoan',
    description:
      'The terms governing use of the MyCaseLoan platform by plaintiffs, attorneys, and funders.',
  },
  privacy: {
    path: '/privacy',
    title: 'Privacy Policy | MyCaseLoan',
    description:
      'How MyCaseLoan collects, uses, and protects the personal and case information you share with the platform.',
  },

  // Below: intentionally kept out of search results. robots.txt disallows
  // crawling these, but Disallow is not noindex — a blocked URL linked from
  // elsewhere can still be indexed as a bare link. These carry the real signal.
  login: {
    path: '/login',
    title: 'Log In | MyCaseLoan',
    description:
      'Log in to your MyCaseLoan account to track your application, review offers, and manage your documents.',
    noindex: true,
  },
  signup: {
    path: '/signup',
    title: 'Create an Account | MyCaseLoan',
    description:
      'Create a MyCaseLoan account as a plaintiff, attorney, or funder to get started on the platform.',
    noindex: true,
  },
  notFound: {
    // The URL varies by definition, so there is nothing to declare canonical.
    path: null,
    title: 'Page Not Found | MyCaseLoan',
    description: 'The page you were looking for does not exist or has moved.',
    noindex: true,
  },

  // Transient auth and token-gated flows. No description: these produce no
  // search snippet because they are never indexed. Several carry single-use
  // tokens in the URL, so `path: null` avoids declaring a canonical for a URL
  // that is unique per recipient.
  forgotPassword: {
    path: '/forgot-password',
    title: 'Reset Your Password | MyCaseLoan',
    noindex: true,
  },
  resetPassword: {
    path: null,
    title: 'Choose a New Password | MyCaseLoan',
    noindex: true,
  },
  verifyEmail: {
    path: null,
    title: 'Verify Your Email | MyCaseLoan',
    noindex: true,
  },
  attorneyInvite: {
    path: null,
    title: 'Attorney Invitation | MyCaseLoan',
    noindex: true,
  },
  referralClaim: {
    path: null,
    title: 'Claim Your Referral | MyCaseLoan',
    noindex: true,
  },
  setupAccount: {
    path: null,
    title: 'Set Up Your Account | MyCaseLoan',
    noindex: true,
  },
  inviteAccept: {
    path: null,
    title: 'Accept Your Invitation | MyCaseLoan',
    noindex: true,
  },
  authCallback: {
    path: null,
    title: 'Signing You In | MyCaseLoan',
    noindex: true,
  },
};

// Case-type landing pages derive their metadata from the content module, so a
// page's title and description live beside the copy they describe.
//
// noindex: true — deliberately held out of search. These pages carry
// legal-adjacent, state-law claims (see the "Sign-off required before launch"
// table in docs/superpowers/specs/2026-07-31-case-type-pages-design.md) that
// have not yet been signed off. The pages are live and linked, just not
// indexed, until that review completes. Removing this flag must happen
// together with re-adding these five URLs to public/sitemap.xml — the two
// changes are one unit of work, enforced by the pageMeta.test.js assertion
// that no noindex page appears in the sitemap.
const CASE_TYPE_PAGE_META = Object.fromEntries(
  CASE_TYPE_PAGES.map((page) => [
    `caseType_${page.slug}`,
    {
      path: page.path,
      title: page.meta.title,
      description: page.meta.description,
    },
  ]),
);

export const PAGE_META = { ...STATIC_PAGE_META, ...CASE_TYPE_PAGE_META };

/** Absolute canonical URL for a metadata entry, or null when it has no stable URL. */
export function canonicalUrl(meta) {
  if (!meta || !meta.path) return null;
  return meta.path === '/' ? `${SITE_URL}/` : `${SITE_URL}${meta.path}`;
}
